import {formatDate} from '@angular/common';
import {PyramidCategory} from 'src/app/components/categories-pyramid-display/categories-pyramid-display.component';
import {
  OverallScoreVm,
  ScoreDevelopmentChipVm,
  ScoreDevelopmentVm,
  TeamOverviewCellVm,
  TeamOverviewColumnVm,
  TeamOverviewRowVm,
  TeamOverviewVm
} from '../models/team-dynamic.vm';

export type ColorMap = { [key: string]: string };

const ICON_BASE = '../../../assets/icons/5D/';
const OVERALL_ICON = '../../../assets/svg/overall-white.svg';

const CATEGORY_ICONS: ColorMap = {
  trust: 'trust-icon.svg',
  conflict: 'conflict-icon.svg',
  commitment: 'commitment-icon.svg',
  accountability: 'accountability-icon.svg',
  results: 'results-icon.svg'
};

export function getIntervalColorName(score: number | null | undefined, intervals: any[]): string {
  if (!score) {
    return 'grey';
  }
  const interval = (intervals ?? []).find((item: any) => item.min <= score && score <= item.max);
  return interval?.indicatorColor?.color ?? 'grey';
}

export function getScoreColor(score: number | null | undefined, intervals: any[], colors: ColorMap): string {
  return colors[getIntervalColorName(score, intervals)] ?? colors['grey'];
}

export function getScoreGradient(score: number | null | undefined, intervals: any[], colors: ColorMap): string {
  return colors[getIntervalColorName(score, intervals) + 'Gradient'] ?? colors['greyGradient'];
}

const FADE_COLORS: ColorMap = {
  green: '#DBF1EA',
  yellow: '#F3EEC7',
  orange: '#FFE7C8',
  red: '#FEE5E5',
  grey: '#DEE4F3'
};

export function getScoreFadeColor(score: number | null | undefined, intervals: any[]): string {
  return FADE_COLORS[getIntervalColorName(score, intervals)] ?? FADE_COLORS['grey'];
}

export function getCategoryIcon(slug: string | undefined): string {
  const key = slug ? Object.keys(CATEGORY_ICONS).find(name => slug.includes(name)) : undefined;
  return key ? ICON_BASE + CATEGORY_ICONS[key] : '';
}

export function mapPyramidCategories(teamDynamic: any, intervals: any[], colors: ColorMap): PyramidCategory[] {
  const details = teamDynamic?.current?.details;
  if (!details?.length) {
    return [];
  }
  const previousScores = mapPreviousScores(teamDynamic?.previous?.details);

  return [...details].reverse().map((detail: any) => {
    const collection = detail.timedQuestionCollection;
    const score = detail.score ?? 0;
    return {
      uuid: collection?.uuid,
      title: (collection?.title ?? '').trim(),
      score,
      icon: getCategoryIcon(collection?.slug),
      color: getScoreColor(score, intervals, colors),
      gradientColor: getScoreGradient(score, intervals, colors),
      trend: getTrend(score, previousScores[collection?.uuid])
    };
  });
}

export function mapOverallScore(teamDynamic: any, teamsCount: number, intervals: any[],
                                colors: ColorMap): OverallScoreVm {
  const score = teamDynamic?.current?.score ?? 0;
  const previousScore = teamDynamic?.previous?.score;
  const hasPrevious = previousScore !== undefined && previousScore !== null;

  return {
    score,
    color: getScoreColor(score, intervals, colors),
    gradientColor: getScoreGradient(score, intervals, colors),
    trend: hasPrevious ? getTrend(score, previousScore) : null,
    diff: hasPrevious ? score - previousScore : null,
    teamsCount
  };
}

export function mapScoreDevelopment(response: any): ScoreDevelopmentVm {
  const points = extractScoreDevelopmentList(response)
    .map((entry: any) => {
      const date = Object.keys(entry ?? {})[0];
      return date ? {date: date, score: entry[date]} : null;
    })
    .filter((point: any) => !!point && point.score !== null && point.score !== undefined)
    .sort((a: any, b: any) => a.date.localeCompare(b.date));

  return {
    labels: points.map((point: any) => formatDate(point.date, 'dd MMM yyyy', 'en-US')),
    values: points.map((point: any) => point.score)
  };
}

export function mapScoreDevelopmentChips(categories: PyramidCategory[], overallTitle: string,
                                         colors: ColorMap): ScoreDevelopmentChipVm[] {
  const overall: ScoreDevelopmentChipVm = {
    uuid: null,
    title: overallTitle,
    icon: OVERALL_ICON,
    color: colors['orange'],
    gradientColor: colors['orangeGradient']
  };

  return [
    overall,
    ...categories.map(category => ({
      uuid: category.uuid,
      title: category.title,
      icon: category.icon,
      color: category.color,
      gradientColor: category.gradientColor
    }))
  ];
}

export function mapTeamOverview(response: any, categories: PyramidCategory[], intervals: any[],
                                colors: ColorMap): TeamOverviewVm {
  const columns = mapTeamOverviewColumns(categories);
  const teams = response?.teamTeamDynamics ?? [];

  return {
    latestCheckIn: getLatestCheckIn(teams),
    columns,
    rows: teams.map((team: any) => mapTeamOverviewRow(team, columns, intervals, colors))
  };
}

function mapTeamOverviewColumns(categories: PyramidCategory[]): TeamOverviewColumnVm[] {
  return [...categories].reverse().map(category => ({
    uuid: category.uuid,
    title: category.title,
    icon: category.icon
  }));
}

function mapTeamOverviewRow(team: any, columns: TeamOverviewColumnVm[], intervals: any[],
                            colors: ColorMap): TeamOverviewRowVm {
  const currentScores = mapDetailScoresByTitle(team?.details);
  const previousScores = mapDetailScoresByTitle(team?.previous?.details);
  const overallScore = team?.score ?? null;
  const overallColor = getScoreColor(overallScore, intervals, colors);

  const cells: TeamOverviewCellVm[] = columns.map(column => {
    const score = currentScores[column.title];
    const hasScore = score !== undefined && score !== null;
    return {
      score: hasScore ? score : null,
      trend: hasScore ? getTrend(score, previousScores[column.title]) : null,
      fadeColor: getScoreFadeColor(hasScore ? score : null, intervals)
    };
  });

  return {
    teamUuid: team?.team?.uuid ?? '',
    teamName: team?.team?.name ?? '',
    cells,
    overallScore,
    overallColor
  };
}

function mapDetailScoresByTitle(details: any[] | undefined): { [key: string]: number } {
  return (details ?? []).reduce((acc: any, detail: any) => {
    const title = (detail?.timedQuestionCollection?.title ?? '').trim();
    if (title) {
      acc[title] = detail.score;
    }
    return acc;
  }, {});
}

function getLatestCheckIn(teams: any[]): string | null {
  const created = teams
    .map((team: any) => team?.created)
    .filter((date: any) => !!date)
    .sort((a: string, b: string) => b.localeCompare(a))[0];

  return created ? formatDate(created.replace(' ', 'T'), 'dd MMM yyyy', 'en-US') : null;
}

function mapPreviousScores(details: any[] | undefined): { [key: string]: number } {
  return (details ?? []).reduce((acc: any, detail: any) => {
    const uuid = detail?.timedQuestionCollection?.uuid;
    if (uuid) {
      acc[uuid] = detail.score;
    }
    return acc;
  }, {});
}

function getTrend(score: number, previousScore: number | undefined): 'up' | 'down' | null {
  if (previousScore === undefined || previousScore === null || score === previousScore) {
    return null;
  }
  return score > previousScore ? 'up' : 'down';
}

function extractScoreDevelopmentList(response: any): any[] {
  if (!response || response.error) {
    return [];
  }
  if (Array.isArray(response.scores)) {
    return response.scores;
  }
  if (Array.isArray(response)) {
    return response;
  }
  const embedded = response._embedded;
  if (embedded) {
    const firstList = Object.keys(embedded).map(key => embedded[key]).find(value => Array.isArray(value));
    return firstList ?? [];
  }
  return [];
}
